<template>
  <Modal
    :title="modalTitle"
    :saving="saving"
    class-name="purchase-orders-modal"
    @hide-modal="$emit('hide-modal')"
    @submit="handleSavePurchaseOrder"
  >
    <ToggleSwitch
      v-if="isEditing"
      v-model="formData.void"
      class="mb-4"
      label-on="Void"
    />

    <div class="flex space-x-3 mb-4">
      <TextField
        v-model="formData.amount_cents"
        currency
        :disable-negative="false"
        strict-currency-input
        sign-before-symbol
        label="Amount"
        name="amount_cents"
        labelClass="flex-1"
      />
      <ChoiceBox
        v-model="formData.po_type"
        name="Vendor"
        label="PO type (Optional)"
        placeholder="Select Type"
        type="po_types"
        class="flex-1"
      />
    </div>

    <div class="flex space-x-3 mb-4">
      <ChoiceBox
        v-model="formData.vendor"
        name="vendor"
        label="Vendor"
        type="vendors"
        placeholder="Select a Vendor"
        :validation-errors="v$.$errors"
        class="flex-1"
      />
      <SelectBox
        v-model="selectedTechnicianId"
        name="Created for"
        label="Created for (Optional)"
        class="flex-1"
        :options="technicians"
        placeholder="Select a Technician"
      />
    </div>

    <div class="flex space-x-3 mb-4">
      <Datepicker
        v-model="selectedPoDate"
        :field-error-message="v$.selectedPoDate.$errors[0]?.$message"
        placeholder="Select Date"
        class="flex-1"
        name="PO Date"
        label="PO Date"
      />
      <div class="flex-1"></div>
    </div>

    <label class="block mb-4">
      <span class="block text-sm font-normal text-gray-700 mb-1">
        Description
      </span>
      <textarea
        v-model="formData.description"
        class="w-full border border-gray-400 rounded-md shadow-sm p-2"
        rows="2"
        placeholder="Description"
      />
    </label>
  </Modal>
</template>

<script>
import { mapActions, mapState } from "vuex";

import ChoiceBox from "@/components/ChoiceBox.vue";
import Datepicker from "@/components/Datepicker.vue";
import SelectBox from "@/components/SelectBox.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import Modal from "../components/Modal.vue";
import TextField from "../components/TextField.vue";

export default {
  components: {
    Modal,
    TextField,
    SelectBox,
    ToggleSwitch,
    ChoiceBox,
    Datepicker
  },
  props: {
    purchaseOrder: {
      type: Object,
      default: null
    }
  },
  emits: ["hide-modal", "save-success"],
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      formData: {
        vendor: {
          required: {
            ...required,
            $message: "Vendor required"
          },
          $autoDirty: true
        }
      },
      selectedPoDate: { required, $autoDirty: true }
    };
  },
  data() {
    return {
      selectedTechnicianId: null,
      formData: { ...this.purchaseOrder },
      saving: false,
      isEditing: false,
      selectedPoDate: null
    };
  },
  computed: {
    ...mapState({
      job: "job",
      technicians: ({ techniciansForJob }) =>
        techniciansForJob.map((tech) => ({
          value: tech.technician_profile_id,
          label: `${tech.first_name} ${tech.last_name}`,
          icon: tech.archived ? "fas fa-archive" : null
        }))
    }),
    modalTitle() {
      return this.purchaseOrder
        ? `Edit Purchase Order #${this.purchaseOrder.po_number}`
        : `Create Purchase Order`;
    }
  },
  mounted() {
    if (this.purchaseOrder && this.purchaseOrder.created_for) {
      this.selectedTechnicianId = this.purchaseOrder.created_for.technician_id;
    }
    this.isEditing = this.purchaseOrder !== null;
    this.selectedPoDate = this.isEditing
      ? this.purchaseOrder.po_date
      : new Date();
    this.fetchTechniciansForJob({ jobId: this.job.id });
  },
  methods: {
    ...mapActions(["createPO", "updatePO", "fetchTechniciansForJob"]),
    handleSavePurchaseOrder() {
      this.v$.$validate().then((valid) => {
        if (valid) {
          const actionFunction = this.isEditing ? this.updatePO : this.createPO;
          this.saving = true;
          const data = {
            purchase_order: {
              ...this.formData,
              po_type: this.formData.po_type || "",
              po_date: this.selectedPoDate,
              created_for: {
                technician_id: this.selectedTechnicianId
              }
            }
          };
          actionFunction({ jobId: this.job.id, data }).then(() => {
            const action = this.isEditing ? "edited" : "created";
            window.notify.success(`Purchase Order ${action} successfully!`);
            this.$emit("hide-modal");
            this.$emit("save-success");
          });
        }
      });
    }
  }
};
</script>

<style lang="scss"></style>
